import Muuri from 'muuri';

// Muuri measures every item and the grid container with getBoundingClientRect(),
// which reports *painted* pixels, but it positions items with translate(), which
// the browser then multiplies by that same ancestor scale. Inside a CSS
// `transform: scale()` ancestor the two disagree, and the error compounds: at
// scale 0.5 a 300px card measures 150, gets slotted 150px below its neighbour,
// and paints 75px below it — so the cards overlap. The container shrinks the
// same way, so the column count collapses on top of that (1200 -> 600 wide with
// unscaled margins = 2 columns where there should be 3).
//
// The no-iframe embed is where this bites: newFrame.js renders the wall straight
// into the host page, so a transformed ancestor can reach it. Framer's canvas and
// preview zoom the page exactly this way, which is why the guide's Method 1 wall
// looks stacked there and correct on the published site. The iframe renderers are
// immune (a cross-document iframe ignores ancestor transforms), and they run this
// same component — at scale 1 it delegates to Muuri's packer with untouched
// numbers, so nothing about the shipped layout changes.
//
// The fix is to hand the packer *layout* pixels instead of painted ones. Slots
// then come back in layout pixels and paint correctly at any zoom, which also
// means they survive the viewer zooming in and out without a relayout.
const DEFAULT_LAYOUT_OPTIONS = Muuri.defaultOptions.layout;

// Sub-pixel noise (offsetWidth is integer-rounded, the rect is not) must not be
// mistaken for a zoom.
const SCALE_EPSILON = 0.005;

// Looser bound for the two-axis agreement check below, because a short grid
// makes integer-rounded offsetHeight a proportionally coarser number.
const AXIS_AGREEMENT_TOLERANCE = 0.02;

// Cumulative scale applied to the element by ancestor transforms: offsetWidth is
// the layout width, the rect is what actually got painted.
function getRenderScale(element) {
  if (!element) {
    return 1;
  }

  const rect = element.getBoundingClientRect();
  const layoutWidth = element.offsetWidth;

  if (!layoutWidth || !rect.width) {
    return 1;
  }

  const scale = rect.width / layoutWidth;

  if (!Number.isFinite(scale) || scale <= 0) {
    return 1;
  }

  // Only a uniform scale can be corrected with a single number. Once the grid
  // has a height to measure, require both axes to agree: a rotated, skewed or
  // scaleX-only ancestor fails this and is handed to Muuri untouched. Nothing
  // here could straighten those out anyway, and falling through leaves them
  // exactly as they render today.
  const layoutHeight = element.offsetHeight;

  if (layoutHeight > 0 && rect.height > 0) {
    const verticalScale = rect.height / layoutHeight;

    if (Math.abs(verticalScale - scale) > AXIS_AGREEMENT_TOLERANCE) {
      return 1;
    }
  }

  return scale;
}

// Muuri `layout` option (grid, layoutId, items, width, height, callback).
export default function scaleAwareLayout(grid, layoutId, items, width, height, callback) {
  const packer = Muuri.defaultPacker;

  // Muuri only calls setOptions itself when `layout` is an options object; a
  // layout *function* owns that, so pass the same defaults the grid would have.
  packer.setOptions(DEFAULT_LAYOUT_OPTIONS);

  const scale = getRenderScale(grid.getElement());

  if (Math.abs(scale - 1) < SCALE_EPSILON) {
    return packer.createLayout(grid, layoutId, items, width, height, callback);
  }

  // The packer reads four size fields off each item, synchronously, before it
  // hands the batch to its worker. Give it plain snapshots so nothing on the
  // live items is mutated, and swap the real items back onto the layout before
  // Muuri consumes it (it positions layout.items, the packer only sizes them).
  //
  // Margins stay as measured: getComputedStyle already resolves percentages
  // against the container's layout width, so they are layout pixels — dividing
  // them too would spread the columns apart by 1/scale.
  const measured = items.map((item) => {
    const margin = item.getMargin();

    return {
      _width: item.getWidth() / scale,
      _height: item.getHeight() / scale,
      _marginLeft: margin.left,
      _marginRight: margin.right,
      _marginTop: margin.top,
      _marginBottom: margin.bottom,
    };
  });

  return packer.createLayout(
    grid,
    layoutId,
    measured,
    width / scale,
    height / scale,
    (layout) => {
      layout.items = items;
      callback(layout);
    },
  );
}
